import '../../common/EsCommon.css';
import './EsAccount.css';

import { useState } from 'react';

import { API_PASSWORD_FORGOT, VIEW_LOGIN, VIEW_REGISTER } from '../../common/EsConstants';
import { esPost } from '../../common/EsFetch';
import { useEsStrings } from '../../common/EsStrings';

/**
 * "Forgot Password" -- where somebody who cannot log in says who they are.
 *
 * <h2>THE SERVER SENDS A CODE AND THIS FILE SENDS A SENTENCE</h2>
 *
 * <p>Three things can happen to an address typed in here: a reset mail goes to
 * it, the account behind it signs in with Google and has no password of ours to
 * reset, or nothing here uses it. esback answers which, as a one-word code, and
 * the wording is below -- the same split {@link EsOauthMessage} argues for at
 * length. The sentence depends on what the page around it already says, and the
 * server does not know that; the code names a state, and a state is the only
 * part of this that both sides have to agree about.
 *
 * <p><b>THE GOOGLE CASE IS A WARNING AND CLAIMS NO MAIL.</b> Tim's words on
 * 2026-09-22: "if it's a google registration, then display a warning in the
 * browser ... don't send the email." It is the one outcome that has to stop
 * somebody rather than inform them -- they are waiting for a message that is
 * never coming, and every minute of that wait looks like mail being slow. It is
 * drawn amber rather than red because nothing has gone wrong: the account is
 * fine and the way in is one page back.
 *
 * <p>An unrecognised code falls back to a sentence rather than to nothing, so a
 * fourth outcome added on the server reads as "we could not answer that"
 * instead of as a form that did nothing.
 *
 * <h2>Why the answer is read off BOTH paths</h2>
 *
 * <p>{@code esPost} resolves on 2xx and throws on anything else, carrying the
 * parsed body on {@code err.body}. Whether esback calls "no such address" a 200
 * with a code or a 404 with one is its own call, and this screen renders the
 * same sentence either way -- a screen that read the code off the success path
 * only would answer "could not reach the server" to an outcome the server had
 * stated perfectly clearly.
 */

/**
 * The six outcomes, keyed on esback's own codes.
 *
 * <p>None of them names the address back to the reader. It is in the box they
 * just typed it into, and a sentence carrying it is a sentence that ends up in
 * a screenshot.
 *
 * <p>The first states the hour (Tim's wording, 2026-10-01). It is esback's number,
 * EsPasswordResetService.WINDOW, so a change there is a change to this sentence
 * and to TX_FORGOT_SENT.
 */
const outcome = ( t ) => ( {
    sent:
        t( 'account-forgot-sent', 'Password reset link sent.\nIt will expire in an hour.' ),
    google:
        t( 'account-forgot-google', 'That account logs in with Google, so there is no Escapade password to reset. Login with Google instead.' ),
    joiner:
        t( 'account-forgot-joiner', 'That address is on the mailing list but has no account yet, so there is no password to reset. Register to make one.' ),
    unknown:
        t( 'account-forgot-unknown', 'No Escapade account uses that address. Check the spelling, or register a new account.' ),
    test:
        t( 'account-forgot-test', 'Test accounts get no mail, so no reset link has been sent.' ),
    unsent:
        t( 'account-forgot-unsent', 'That reset mail could not be sent. Please try again.' ),
} );

const outcomeFallback = ( t ) => t( 'account-forgot-fallback', 'Sorry — that request could not be answered. Please try again.' );

// Drawn amber: the address was RECOGNISED and no mail is coming anyway, so the
// reader has to do something else. Nothing has gone wrong -- both accounts are
// fine -- which is why this is not the error styling. `unknown` is deliberately
// not here: a mistyped address is an ordinary answer, not a warning.
const NOTHING_IS_COMING = new Set( [ 'google', 'joiner', 'test' ] );

// The one outcome that IS a failure. esback tried to send and SES refused, so
// unlike every other code here the fix is to press the button again -- which is
// what the error styling means everywhere else on this screen family.
const SEND_FAILED = 'unsent';

const noticeClass = ( pCode ) =>
    pCode === SEND_FAILED ? 'formError'
        : NOTHING_IS_COMING.has( pCode ) ? 'accountWarning'
        : 'accountNotice';

/**
 * The sentence for a code, or for whatever esback said instead of one.
 *
 * <p>A {@code msg} beats the fallback because esback's error envelope carries
 * one and it is usually more specific than "please try again"; the fallback is
 * for a body that carried neither, which is what a whitelabel error page comes
 * back as.
 */
function forgotSentence( pCode, pMessage, t ) {
    return outcome( t )[ pCode ] ?? pMessage ?? outcomeFallback( t );
}

export default function EsForgot() {
    const t = useEsStrings();

    const [ email, setEmail ] = useState( '' );

    // What esback answered, as { code, text }. The code is kept beside the
    // sentence so the Google case can be DRAWN differently without the styling
    // having to recognise its own wording.
    const [ answer, setAnswer ] = useState( null );

    // This side's own refusal: an empty box, or a request that never landed.
    // Separate from the answer above because they are different claims -- one
    // is what happened to the address, the other is that nothing happened at
    // all -- and showing both at once would contradict itself.
    const [ error, setError ] = useState( '' );

    // Pressed twice is a second mail, and a mail cannot be recalled.
    const [ sending, setSending ] = useState( false );

    const handleSubmit = async ( pEvent ) => {
        pEvent.preventDefault();

        setAnswer( null );

        if ( !email ) {
            setError( t( 'account-forgot-email-required', 'Email address is required.' ) );
            return;
        }

        setError( '' );
        setSending( true );

        try {
            const aBody = await esPost( API_PASSWORD_FORGOT, { email } );

            setAnswer( {
                code: aBody?.outcome,
                text: forgotSentence( aBody?.outcome, aBody?.msg, t ),
            } );
        }
        catch ( pFailure ) {
            // The answer may be HERE rather than above -- see the class note.
            // Only a throw carrying no body of ours is a failure to report as
            // one; pFailure.message on a whitelabel error page is a JSON parse
            // complaint about an angle bracket, which is no use to anybody.
            if ( pFailure.body?.outcome ) {
                setAnswer( {
                    code: pFailure.body.outcome,
                    text: forgotSentence( pFailure.body.outcome, pFailure.body.msg, t ),
                } );
            }
            else {
                // NEVER pFailure.message here. esWrite's `written` does a bare
                // JSON.parse, so a whitelabel error page throws a SyntaxError
                // whose message is a complaint about an angle bracket -- which
                // is how React error #31's cousin gets onto a screen. The
                // status is the only part of that worth showing.
                setError( pFailure.body?.msg
                    || ( pFailure.status
                        ? t( 'common-server-error', 'Sorry — the server could not answer that (error {status}).', { status: String( pFailure.status ) } )
                        : t( 'common-server-unreachable', 'Sorry — could not reach the server.' ) ) );
            }
        }
        finally {
            setSending( false );
        }
    };

    return (
        <section data-testid="es-forgot-page" className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="flex flex-wrap mx-[-16px]">
                    <div className="w-full px-4">
                        <div className="mx-auto rounded-md p-12 max-w-[500px] sm:p-[60px]">
                            <h3 data-testid="es-forgot-heading" className="mb-3 text-center text-2xl font-bold text-heading sm:text-3xl">
                                { t( 'account-forgot-heading', 'Forgot Password' ) }
                            </h3>
                            <p data-testid="es-forgot-text" className="formSubHeader">
                                { t( 'account-forgot-text', 'We will email you a link to choose a new password.' ) }
                            </p>

                            <form data-testid="es-forgot-form" onSubmit={ handleSubmit }>
                                <div className="mb-8">
                                    <label className="accountLabel" htmlFor="email">
                                        { t( 'account-forgot-email', 'Email Address' ) }
                                    </label>
                                    { /* Controlled, because handleSubmit validates the
                                         value rather than reading it back off the
                                         element -- and because the box keeps what was
                                         typed after an answer comes back, so a
                                         mistyped address can be corrected rather than
                                         retyped. */ }
                                    <input autoComplete="email"
                                        onChange={ ( pEvent ) => setEmail( pEvent.target.value ) }
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={ email }
                                        data-testid="es-forgot-email"
                                        className="accountField" />
                                </div>
                                <div className="mb-6 pt-4">
                                    <button
                                        type="submit"
                                        disabled={ sending }
                                        data-testid="es-forgot-submit"
                                        className="buttonSubmit flex w-full items-center justify-center">
                                        { t( 'account-forgot-submit', 'Send reset link' ) }
                                    </button>
                                </div>
                            </form>

                            { error &&
                                <p data-testid="es-forgot-error" className="formError"> { error } </p> }

                            { answer &&
                                <p data-testid="es-forgot-message"
                                   data-outcome={ answer.code ?? 'unrecognised' }
                                   className={ noticeClass( answer.code ) + ' whitespace-pre-line' }>
                                    { answer.text }
                                </p> }

                            <p className="formStatus mt-8">
                                <a href={ VIEW_LOGIN } data-testid="es-forgot-login-link" className="text-accent hover:underline">
                                    { t( 'account-forgot-login', 'Back to login' ) }
                                </a>
                                {' · '}
                                <a href={ VIEW_REGISTER } data-testid="es-forgot-register-link" className="text-accent hover:underline">
                                    { t( 'account-forgot-register', 'Register' ) }
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
